import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, FolderTree, LogOut, PanelLeftClose } from 'lucide-react'
import {
  Link,
  Outlet,
  RouterProvider,
  useMatches,
  useNavigate,
} from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  Button,
  CommandPalette,
  type CommandEntry,
  EmptyState,
  MobileSidebarDrawer,
  Popover,
  PopoverMenu,
  PopoverMenuItem,
  PopoverTrigger,
  Sidebar,
  SidebarSection,
  SidebarSectionList,
  SidebarItem,
  ToastContainer,
  TooltipProvider,
  Topbar,
  type BreadcrumbSegment,
} from '@/components/ui'
import { useIsMobile } from '@/lib/hooks/useIsMobile'
import { usePreferences } from '@/shell/stores/preferences'
import {
  AuthProvider,
  buildRouter,
  canAccessModule,
  getHomePath,
  moduleRegistry,
  useAuth,
  useKeyboardShortcuts,
  type AuthUser,
  type NavBadge,
  type NavSection,
  type ShortcutBinding,
  type ValidatedModule,
} from '@/shell/module-system'
import { logout, useCurrentUser } from '@/shell/services/auth'
import { ImpersonationBanner } from './ImpersonationBanner'
import { OdooStatusBanner } from './OdooStatusBanner'
import { P0AlertBanner } from './P0AlertBanner'
import { LoginScreen } from './LoginScreen'
import { VendorLoginScreen } from './VendorLoginScreen'

/**
 * Composed app shell — reads the manifest registry, builds a TanStack
 * Router, renders the Sidebar + Topbar + CommandPalette + Toaster
 * around the outlet.
 *
 * No module-specific code lives here. Adding / removing a module means
 * dropping / deleting a folder under `src/modules/`.
 */
// One React Query client per app lifetime. Modules consume it via the
// `useQuery` hooks in their own `api/queries.ts` — no module wires the
// provider itself.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

/**
 * Canonical section render order. The sidebar iterates this list;
 * sections with zero accessible modules render nothing (the section-
 * level UI contract from enhancement-01 D-3).
 */
const SECTION_ORDER: ReadonlyArray<{ key: NavSection; label: string }> = [
  { key: 'home', label: 'Home' },
  { key: 'executive', label: 'Executive' },
  { key: 'sales', label: 'Sales' },
  { key: 'customer_service', label: 'Customer Service' },
  { key: 'purchasing', label: 'Purchasing' },
  { key: 'regulatory', label: 'Regulatory' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'logistics', label: 'Logistics' },
  { key: 'formulations', label: 'Formulations' },
  { key: 'finance', label: 'Finance' },
  { key: 'admin', label: 'Admin' },
  { key: 'vendors', label: 'Uviva' },
]

export function Shell() {
  // QueryClientProvider has to wrap the auth gate — `useCurrentUser`
  // (the gate's data source) is a useQuery hook.
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthGate />
        <ToastContainer />
      </TooltipProvider>
    </QueryClientProvider>
  )
}

/**
 * Auth-aware render fork — Sprint 02 W-1.
 *
 *  - Loading state: blank (brief by design; React Query returns < 50 ms
 *    with a 5-min staleTime once the first call completes)
 *  - 401 / no cookie: LoginScreen — user clicks "Log in with Google"
 *  - 5xx / network: generic error banner with retry
 *  - 200 with user: renders the router inside AuthProvider
 */
function AuthGate() {
  const { user, isLoading, isAuthError, error, refetch } = useCurrentUser()

  const vendorLoginConfig = VENDOR_LOGIN_PATHS.find((v) =>
    window.location.pathname.startsWith(v.pathPrefix),
  )
  if (isLoading && !user) return <SplashLoading />
  if (isAuthError) {
    return vendorLoginConfig ? (
      <VendorLoginScreen
        title={vendorLoginConfig.title}
        subtitle={vendorLoginConfig.subtitle}
        landingPath={vendorLoginConfig.landingPath}
      />
    ) : (
      <LoginScreen />
    )
  }
  if (error || !user) return <LoadError error={error} onRetry={() => refetch()} />

  return <AuthenticatedShell user={user} />
}

/**
 * Password-login ("vendor-flagged") entry points, keyed by URL path
 * prefix — mirrors the `vendor: True` rbac flag (app/rbac.py). Each
 * entry's own bookmarked portal URL decides which copy/landing page
 * `VendorLoginScreen` renders; the login endpoint itself doesn't care
 * which role hits it, only that the account is vendor-flagged.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure data colocated with AuthGate so tests can pin the path→landing mapping without rendering the auth-gated component tree.
export const VENDOR_LOGIN_PATHS: ReadonlyArray<{
  pathPrefix: string
  title: string
  subtitle: string
  landingPath: string
}> = [
  {
    pathPrefix: '/uviva',
    title: 'Uviva Portal',
    subtitle: 'SPI partner portal — Uviva accounts only.',
    landingPath: '/uviva',
  },
  {
    pathPrefix: '/outside-sales',
    title: 'SPI Ops',
    subtitle: 'Outside sales rep sign-in.',
    landingPath: '/orders',
  },
]

/**
 * Minimal loading placeholder while `/api/auth/me` is in flight on
 * first mount. Intentionally sparse — a spinner-heavy splash on every
 * load is noise.
 */
function SplashLoading() {
  return (
    <div
      role="status"
      aria-label="Loading SPI Ops"
      className="flex h-dvh items-center justify-center bg-canvas text-tertiary"
    >
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-accent-subtle text-accent-text">
        <FolderTree size={20} aria-hidden="true" />
      </span>
    </div>
  )
}

function LoadError({
  error,
  onRetry,
}: {
  error: unknown
  onRetry: () => void
}) {
  const message = error instanceof Error ? error.message : 'Unknown error'
  return (
    <div className="flex h-dvh items-center justify-center bg-canvas">
      <div className="max-w-md px-6">
        <EmptyState
          variant="error"
          title="Couldn't reach the server"
          description={
            <>
              <p className="mb-2">{message}</p>
              <p className="text-xs text-tertiary">
                The middleware at /api/auth/me didn't respond. Try again;
                if it persists, check that the middleware container is
                running.
              </p>
            </>
          }
          action={
            <Button variant="primary" onClick={onRetry}>
              <AlertTriangle size={12} /> Retry
            </Button>
          }
        />
      </div>
    </div>
  )
}

/**
 * Stable router-rebuild key for `AuthenticatedShell`. The router only
 * needs to rebuild on actual identity transitions (logout/login,
 * impersonation start/end). Depending on the full `user` reference
 * would rebuild on every `/api/auth/me` refetch — including the
 * focus-driven refetch after staleTime — because `useCurrentUser`
 * returns a fresh `AuthUser` object each call (Set-typed `permissions`
 * defeats TanStack Query's structural sharing). A new router instance
 * unmounts the entire matched-route tree via `RouterProvider`'s
 * `CatchBoundary` reset, wiping every page's local `useState`.
 *
 * Exported for unit-test coverage that pins the invariant.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper colocated with AuthenticatedShell so the unit test pinning the no-remount invariant can exercise it without spinning the whole router up.
export function userIdentityKey(user: AuthUser): string {
  return `${user.uid}::${user.impersonating?.adminEmail ?? ''}`
}

function AuthenticatedShell({ user }: { user: AuthUser }) {
  // Live user is read through a ref so `beforeLoad` guards still see
  // the freshest permissions without forcing a rebuild.
  const userRef = useRef(user)
  useEffect(() => {
    userRef.current = user
  }, [user])

  const identityKey = userIdentityKey(user)
  const router = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs -- userRef.current is intentionally read inside the getAuth closure, which TanStack Router calls later from its beforeLoad guards — never during this render. Reading the live user any other way would force a router rebuild on every auth refetch, which would unmount every page's local useState. See `userIdentityKey` docblock above.
      buildRouter({
        rootComponent: ShellLayout,
        getAuth: () => userRef.current,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the rebuild gate is identityKey by design: identity transitions (login/logout, impersonate start/end) force a router rebuild, ordinary auth refetches do not. The factory only reads userRef.current, so adding identityKey to the closure isn't necessary — but it MUST stay in the dep array to trigger the rebuild.
    [identityKey],
  )

  return (
    <AuthProvider user={user}>
      <RouterProvider router={router} />
    </AuthProvider>
  )
}

/** Root-route layout. Sidebar + Topbar + CommandPalette + <Outlet />. */
function ShellLayout() {
  const user = useAuth()
  const sidebarCollapsed = usePreferences((s) => s.sidebarCollapsed)
  const setSidebarCollapsed = usePreferences((s) => s.setSidebarCollapsed)
  const sidebarSectionsCollapsed = usePreferences((s) => s.sidebarSectionsCollapsed)
  const setSectionCollapsed = usePreferences((s) => s.setSectionCollapsed)
  const sidebarOrder = usePreferences((s) => s.sidebarOrder)
  const setSidebarOrder = usePreferences((s) => s.setSidebarOrder)
  const sidebarSectionOrder = usePreferences((s) => s.sidebarSectionOrder)
  const setSidebarSectionOrder = usePreferences((s) => s.setSidebarSectionOrder)
  const [paletteOpen, setPaletteOpen] = useState(false)
  // Transient — deliberately not routed through `usePreferences`. That
  // store persists the desktop icon-rail collapse preference; the mobile
  // off-canvas drawer always starts closed and never round-trips.
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const matches = useMatches()

  // Modules the user can actually see — filtered by permission.
  const visible = useMemo(
    () =>
      moduleRegistry.filter(
        (m) => m.navSection !== 'hidden' && canAccessModule(user, m),
      ),
    [user],
  )

  // Drives both the Topbar "?" icon and the per-module help link below —
  // a role without sidebar access to Help (e.g. outside_sales_rep) gets
  // no other affordance pointing at a page it can't open either.
  const canSeeHelp = visible.some((m) => m.id === 'help')

  // Sprint 13a — role-based default-collapsed map. For each section,
  // if any of the user's accessible modules in that section sets
  // `navCollapsedForRoles` and the user has any of those roles, the
  // section starts collapsed. Persisted user preference (toggle clicks)
  // overrides the default.
  const roleDefaultCollapsed = useMemo<Partial<Record<NavSection, boolean>>>(() => {
    const out: Partial<Record<NavSection, boolean>> = {}
    for (const m of visible) {
      if (out[m.navSection]) continue // already marked
      const flagged = m.navCollapsedForRoles ?? []
      if (flagged.some((role) => user.permissions.has(role))) {
        out[m.navSection] = true
      }
    }
    return out
  }, [visible, user])

  const isSectionCollapsed = (section: NavSection): boolean => {
    const persisted = sidebarSectionsCollapsed[section]
    if (typeof persisted === 'boolean') return persisted
    return roleDefaultCollapsed[section] ?? false
  }

  /**
   * Sprint 13b — apply per-user ordering on top of the manifest's
   * `navOrder`. Modules listed in `sidebarOrder[section]` come first
   * in the given order; modules not in the list fall back to navOrder
   * ascending. Stable for ties.
   */
  const orderModulesForSection = (
    section: NavSection,
    sectionModules: ReadonlyArray<ValidatedModule>,
  ): ReadonlyArray<ValidatedModule> => {
    const userOrder = sidebarOrder[section]
    if (!userOrder || userOrder.length === 0) return sectionModules
    const indexById = new Map<string, number>()
    userOrder.forEach((id, i) => indexById.set(id, i))
    return [...sectionModules].sort((a, b) => {
      const ai = indexById.has(a.id) ? indexById.get(a.id)! : Infinity
      const bi = indexById.has(b.id) ? indexById.get(b.id)! : Infinity
      if (ai !== bi) return ai - bi
      return a.navOrder - b.navOrder
    })
  }

  /**
   * Category (section) drag-to-reorder — same shape as
   * `orderModulesForSection` one level up. Starts from `SECTION_ORDER`
   * filtered down to sections with at least one visible module for
   * this user (this is also what excludes empty sections from ever
   * being a draggable row — they're never in the array being sorted),
   * then reorders by the user's saved `sidebarSectionOrder`, falling
   * back to each section's original `SECTION_ORDER` position for
   * sections not listed or for ties (sections don't have a numeric
   * `navOrder`; their canonical order IS their `SECTION_ORDER` index).
   */
  const orderSections = (
    visibleModules: ReadonlyArray<ValidatedModule>,
  ): ReadonlyArray<{ key: NavSection; label: string }> => {
    const nonEmpty = SECTION_ORDER.filter(({ key }) =>
      visibleModules.some((m) => m.navSection === key),
    )
    if (sidebarSectionOrder.length === 0) return nonEmpty
    const indexById = new Map<string, number>()
    sidebarSectionOrder.forEach((id, i) => indexById.set(id, i))
    const defaultIndex = new Map<NavSection, number>()
    nonEmpty.forEach(({ key }, i) => defaultIndex.set(key, i))
    return [...nonEmpty].sort((a, b) => {
      const ai = indexById.has(a.key) ? indexById.get(a.key)! : Infinity
      const bi = indexById.has(b.key) ? indexById.get(b.key)! : Infinity
      if (ai !== bi) return ai - bi
      return defaultIndex.get(a.key)! - defaultIndex.get(b.key)!
    })
  }

  // Global keyboard shortcuts. Leader sequences from module manifests +
  // the built-in Cmd+B / Cmd+K / ? / Cmd+Shift+D set.
  const shortcuts = useMemo<ShortcutBinding[]>(() => {
    const bindings: ShortcutBinding[] = [
      {
        combo: 'Cmd+B',
        handler: () => setSidebarCollapsed(!usePreferences.getState().sidebarCollapsed),
      },
      { combo: '?', handler: () => setPaletteOpen(true) },
    ]
    for (const m of visible) {
      if (!m.keyboardShortcut) continue
      bindings.push({
        combo: m.keyboardShortcut,
        handler: () => navigate({ to: m.resolvedNamespace }),
      })
    }
    return bindings
  }, [visible, navigate, setSidebarCollapsed])
  useKeyboardShortcuts(shortcuts)

  // Command palette entries — every module's own entries + auto-generated
  // "Go to {module}" for any module without its own.
  const paletteEntries = useMemo<CommandEntry[]>(() => {
    const entries: CommandEntry[] = []
    for (const m of visible) {
      const ownEntries = m.commandPaletteEntries ?? []
      if (ownEntries.length > 0) {
        for (const e of ownEntries) {
          const href = e.path.startsWith('/')
            ? `${m.resolvedNamespace}${e.path === '/' ? '' : e.path}`
            : `${m.resolvedNamespace}/${e.path}`
          entries.push({
            id: `${m.id}:${e.label}`,
            label: e.label,
            meta: e.shortcut ?? m.keyboardShortcut,
            keywords: e.keywords,
            group: 'Modules',
            icon: m.icon,
            onSelect: () => navigate({ to: href as string }),
          })
        }
      } else {
        entries.push({
          id: m.id,
          label: `Go to ${m.name}`,
          meta: m.keyboardShortcut,
          group: 'Modules',
          icon: m.icon,
          onSelect: () => navigate({ to: m.resolvedNamespace }),
        })
      }
    }
    return entries
  }, [visible, navigate])

  const currentPath =
    typeof window !== 'undefined' ? window.location.pathname : '/'
  const breadcrumb = useBreadcrumb(visible, matches)
  const currentModuleId = useCurrentModuleId(visible, matches)

  // Shared between the desktop rail and the mobile off-canvas drawer —
  // only the `collapsed` mode and the post-navigate close differ.
  // `collapsed` is always `false` on mobile: icon-only nav isn't a usable
  // touch target, and `onNavigate` closes the drawer after a tap.
  const renderSidebarSections = (collapsed: boolean, onNavigate?: () => void) => {
    const orderedSections = orderSections(visible)
    // Category drag mirrors the item-level guard: only meaningful (and
    // only wired up) when the rail is expanded and there's more than
    // one section to reorder. The icon-only rail renders no section
    // headers to drag in the first place.
    const sectionIds =
      !collapsed && orderedSections.length > 1
        ? orderedSections.map(({ key }) => key)
        : undefined

    const sections = orderedSections.map(({ key, label }) => {
      const sectionModules = orderModulesForSection(
        key,
        visible.filter((m) => m.navSection === key),
      )
      return (
        <NavSection
          key={key}
          label={label}
          modules={sectionModules}
          currentPath={currentPath}
          collapsed={collapsed}
          sectionCollapsed={isSectionCollapsed(key)}
          onToggleSectionCollapsed={() => setSectionCollapsed(key, !isSectionCollapsed(key))}
          onReorder={(nextIds) => setSidebarOrder(key, nextIds)}
          onNavigate={onNavigate}
          sectionDragId={sectionIds ? key : undefined}
        />
      )
    })

    if (!sectionIds) return sections
    return (
      <SidebarSectionList
        ids={sectionIds}
        onReorder={(nextKeys) => setSidebarSectionOrder(nextKeys)}
      >
        {sections}
      </SidebarSectionList>
    )
  }

  return (
    <div className="flex h-full">
      {!isMobile ? (
        <Sidebar
          collapsed={sidebarCollapsed}
          header={
            <div className="flex w-full items-center justify-between">
              <ShellBrand collapsed={sidebarCollapsed} user={user} />
              <SidebarCollapseToggle
                collapsed={sidebarCollapsed}
                onCollapse={() => setSidebarCollapsed(true)}
              />
            </div>
          }
          footer={!sidebarCollapsed ? <UserFooter /> : null}
        >
          {renderSidebarSections(sidebarCollapsed)}
        </Sidebar>
      ) : (
        <MobileSidebarDrawer
          open={mobileNavOpen}
          onOpenChange={setMobileNavOpen}
          header={<ShellBrand collapsed={false} user={user} />}
          footer={<UserFooter />}
        >
          {renderSidebarSections(false, () => setMobileNavOpen(false))}
        </MobileSidebarDrawer>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          breadcrumb={breadcrumb}
          onOpenCommandPalette={() => setPaletteOpen(true)}
          onExpandSidebar={
            !isMobile && sidebarCollapsed ? () => setSidebarCollapsed(false) : undefined
          }
          onOpenMobileNav={isMobile ? () => setMobileNavOpen(true) : undefined}
          actions={
            <>
              {canSeeHelp && currentModuleId ? (
                <PerModuleHelpLink moduleId={currentModuleId} />
              ) : null}
              {canSeeHelp ? <TopbarHelpButton /> : null}
            </>
          }
        />
        <ImpersonationBanner />
        <OdooStatusBanner />
        <P0AlertBanner />
        <main className="flex-1 overflow-auto bg-canvas">
          <Outlet />
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        entries={paletteEntries}
      />
    </div>
  )
}

function NavSection({
  label,
  modules,
  currentPath,
  collapsed,
  sectionCollapsed,
  onToggleSectionCollapsed,
  onReorder,
  onNavigate,
  sectionDragId,
}: {
  label: string
  modules: ReadonlyArray<ValidatedModule>
  currentPath: string
  collapsed: boolean
  sectionCollapsed?: boolean
  onToggleSectionCollapsed?: () => void
  /** Sprint 13b — fires on drag-end with the new id order. */
  onReorder?: (next: ReadonlyArray<string>) => void
  /** Mobile-only — closes the off-canvas drawer after a nav item is tapped. */
  onNavigate?: () => void
  /** Category-level drag id — set when this section may be reordered among siblings. */
  sectionDragId?: string
}) {
  if (modules.length === 0) return null
  // Sprint 13b — sortable only when there's more than one item to
  // reorder AND the sidebar is in expanded mode. Single-item sections
  // skip the dnd wiring (no value).
  const sortableIds =
    !collapsed && modules.length > 1 && onReorder
      ? modules.map((m) => m.id)
      : undefined
  return (
    <SidebarSection
      label={label}
      collapsed={collapsed}
      sectionCollapsed={sectionCollapsed}
      onToggleSectionCollapsed={onToggleSectionCollapsed}
      sortableIds={sortableIds}
      onReorder={onReorder}
      dragId={sectionDragId}
    >
      {modules.map((m) => (
        <NavItem
          key={m.id}
          module={m}
          currentPath={currentPath}
          collapsed={collapsed}
          dragId={sortableIds ? m.id : undefined}
          onNavigate={onNavigate}
        />
      ))}
    </SidebarSection>
  )
}

function NavItem({
  module,
  currentPath,
  collapsed,
  dragId,
  onNavigate,
}: {
  module: ValidatedModule
  currentPath: string
  collapsed: boolean
  dragId?: string
  onNavigate?: () => void
}) {
  // A module's badge hook is fixed for its lifetime, so the branch is stable.
  return module.useNavBadge ? (
    <BadgedNavItem module={module} currentPath={currentPath} collapsed={collapsed} dragId={dragId}
      onNavigate={onNavigate} useBadge={module.useNavBadge} />
  ) : (
    <PlainNavItem module={module} currentPath={currentPath} collapsed={collapsed} dragId={dragId}
      onNavigate={onNavigate} />
  )
}

type NavItemProps = {
  module: ValidatedModule
  currentPath: string
  collapsed: boolean
  dragId?: string
  onNavigate?: () => void
}

function BadgedNavItem({ useBadge, ...props }: NavItemProps & { useBadge: () => NavBadge | null }) {
  const badge = useBadge()
  return <PlainNavItem {...props} badge={badge && badge.count > 0 ? badge : null} />
}

function PlainNavItem({ module, currentPath, collapsed, dragId, onNavigate, badge }: NavItemProps & { badge?: NavBadge | null }) {
  const navigate = useNavigate()
  const active = currentPath === module.resolvedNamespace || currentPath.startsWith(`${module.resolvedNamespace}/`)
  return (
    <SidebarItem
      icon={module.icon}
      label={module.shortName ?? module.name}
      shortcut={module.keyboardShortcut}
      collapsed={collapsed}
      active={active}
      dragId={dragId}
      badge={badge ? <span title={badge.title} aria-label={badge.title}>{badge.count}</span> : undefined}
      badgeTone={badge?.tone}
      onClick={() => {
        navigate({ to: module.resolvedNamespace })
        onNavigate?.()
      }}
    />
  )
}

function ShellBrand({ collapsed, user }: { collapsed: boolean; user: AuthUser }) {
  return (
    <Link
      to={getHomePath(user)}
      className="flex items-center gap-2 text-sm font-semibold text-primary no-underline"
    >
      <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-xs bg-accent text-on-accent">
        <FolderTree size={14} />
      </span>
      {!collapsed ? <span>SPI Ops</span> : null}
    </Link>
  )
}

/**
 * Always-visible collapse affordance in the sidebar header, top-right
 * per `01-layout-system.md:54`. Collapsing was previously reachable
 * only via the undiscoverable `Cmd+B` shortcut — `Topbar`'s "Expand
 * sidebar" button already covers the reverse direction (rendered only
 * while collapsed), so this only needs to render while expanded.
 * Exported so it's unit-testable without mounting the full
 * authenticated shell tree.
 */
// eslint-disable-next-line react-refresh/only-export-components -- exported for direct unit-test coverage; see docblock above.
export function SidebarCollapseToggle({
  collapsed,
  onCollapse,
}: {
  collapsed: boolean
  onCollapse: () => void
}) {
  if (collapsed) return null
  return (
    <button
      type="button"
      aria-label="Collapse sidebar"
      title="Collapse sidebar (Cmd/Ctrl+B)"
      onClick={onCollapse}
      className="inline-flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
    >
      <PanelLeftClose size={14} aria-hidden="true" />
    </button>
  )
}

function TopbarHelpButton() {
  return (
    <a
      href="/help"
      title="Help (g ?)"
      aria-label="Open Help"
      className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-transparent text-tertiary hover:border-border-subtle hover:bg-surface-hover hover:text-primary"
    >
      {/* Lucide BookOpen at 14px to match the topbar's icon scale */}
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    </a>
  )
}

function PerModuleHelpLink({ moduleId }: { moduleId: string }) {
  // Always renders — opens /help/user-guides/<id> in a new tab so the
  // operator's working context is preserved. Repointed from the dev
  // /help/modules/<id> tree on 2026-05-10 (sprint-user-guides) so the
  // ? icon answers "how do I use THIS page" instead of "what CARLA
  // tools back this page". If no user guide exists for that id, the
  // help page surfaces a clean 404; admins still get the dev reference
  // via the sidebar's collapsible "Developer reference" group.
  return (
    <a
      href={`/help/user-guides/${moduleId}`}
      target="_blank"
      rel="noopener noreferrer"
      title={`Help for this module (opens in new tab)`}
      aria-label={`Help for the ${moduleId} module`}
      className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-transparent text-tertiary hover:border-border-subtle hover:bg-surface-hover hover:text-primary"
    >
      {/* Lucide HelpCircle */}
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    </a>
  )
}

function UserFooter() {
  const user = useAuth()
  const initials = (user.name ?? user.email ?? 'U')
    .split(/\s+/)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('')
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        className="flex w-full items-center gap-2 rounded-sm px-1 py-1 text-left hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-focus"
      >
        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-accent-subtle text-2xs font-semibold text-accent-text">
          {initials || '·'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-primary">{user.name ?? 'User'}</div>
          <div className="truncate text-2xs text-tertiary">{user.email ?? ''}</div>
        </div>
      </PopoverTrigger>
      <PopoverMenu align="start" side="top">
        <PopoverMenuItem icon={LogOut} danger onSelect={logout}>
          Log out
        </PopoverMenuItem>
      </PopoverMenu>
    </Popover>
  )
}

function useBreadcrumb(
  visible: ReadonlyArray<ValidatedModule>,
  matches: ReturnType<typeof useMatches>,
): BreadcrumbSegment[] {
  // We track route path changes via `matches`. The shell re-renders on
  // navigation, so this just maps the current path to a breadcrumb.
  const [, setTick] = useState(0)
  useEffect(() => {
    const onNav = () => setTick((t) => t + 1)
    window.addEventListener('popstate', onNav)
    return () => window.removeEventListener('popstate', onNav)
  }, [])

  const path = matches[matches.length - 1]?.pathname ?? '/'
  if (path === '/') return []
  const module = visible.find(
    (m) => path === m.resolvedNamespace || path.startsWith(`${m.resolvedNamespace}/`),
  )
  if (!module) return []
  const segments: BreadcrumbSegment[] = [{ label: sectionLabel(module.navSection) }]
  segments.push({ label: module.name })
  return segments
}

/**
 * Returns the id of the module the operator is currently viewing, or
 * null. Used to wire the per-module `?` icon in the Topbar that
 * deep-links to /help/modules/<id>.
 */
function useCurrentModuleId(
  visible: ReadonlyArray<ValidatedModule>,
  matches: ReturnType<typeof useMatches>,
): string | null {
  const [, setTick] = useState(0)
  useEffect(() => {
    const onNav = () => setTick((t) => t + 1)
    window.addEventListener('popstate', onNav)
    return () => window.removeEventListener('popstate', onNav)
  }, [])
  const path = matches[matches.length - 1]?.pathname ?? '/'
  if (path === '/' || path.startsWith('/help')) return null
  const module = visible.find(
    (m) => path === m.resolvedNamespace || path.startsWith(`${m.resolvedNamespace}/`),
  )
  return module ? module.id : null
}

function sectionLabel(section: ValidatedModule['navSection']): string {
  // Exhaustive switch over the 10-section model. TypeScript's `never`
  // check at the bottom forces any future enum widening to update
  // this function at compile time.
  switch (section) {
    case 'home':
      return 'Home'
    case 'executive':
      return 'Executive'
    case 'sales':
      return 'Sales'
    case 'customer_service':
      return 'Customer Service'
    case 'purchasing':
      return 'Purchasing'
    case 'regulatory':
      return 'Regulatory'
    case 'warehouse':
      return 'Warehouse'
    case 'logistics':
      return 'Logistics'
    case 'formulations':
      return 'Formulations'
    case 'finance':
      return 'Finance'
    case 'admin':
      return 'Admin'
    case 'vendors':
      return 'Uviva'
    case 'hidden':
      return ''
    default: {
      const _exhaustive: never = section
      return _exhaustive
    }
  }
}
